import axiosInstance from './axios-instance';

// Interface para os parâmetros de filtro
export interface FilterParams {
  limit?: number;
  franchiseId?: string;
  estateId?: string;
  estateUnitId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
}

// Interfaces para dados detalhados de seguros de fiança
export interface BondInsuranceDetailData {
  // Evolução de análises ao longo do tempo (datas como chaves, contagens como valores)
  analysisEvolution: Record<string, number>;

  // Atividade do cliente (novos, renovações, cancelamentos)
  clientActivity: {
    newClients: number;
    renewals: number;
    cancellations: number;
  };

  // Atividade mensal (meses como chaves YYYY-MM, contagens como valores)
  monthlyActivity: Record<string, number>;
}

// Interface para dados de desempenho por imobiliária
export interface EstatePerformance {
  estateId: string;
  estateName: string;
  value: number;
}

// Interface para dados consolidados da tabela
export interface TableData {
  estateId: string;
  estateName: string;
  franchiseId: string;
  franchiseName: string;
  bondInsurancePolicies: number; // Apólices contratadas (Seguro Fiança)
  fireInsurancePolicies: number; // Seguros contratados (Seguro Incêndio)
  capitalizationContracts: number; // Títulos contratados (Capitalização)
  totalIssued: number;
  totalCanceled: number;
  totalActive: number;
  totalAmount: number;
  totalInvoiced: number;
  totalComission: number;
  totalComissionEstate: number;
  totalComissionBroker: number;
  claims: number; // Sinistros abertos
}

// Interface para resposta da API de dados da tabela
export interface TableDataResponse {
  data: TableData[];
  total: number;
}

// Adicionar a interface para os dados do seguro fiança
export interface BondInsuranceData {
  estateId: string;
  estateName: string;
  franchiseName: string;
  cpfCnpj?: string;
  analysisCount: number;
  policiesCount: number;
  issuedCount: number;
  renewedCount: number;
  approvedCount: number;
  totalActivePremiums: number;
  managerName: string;
  rejectedCount: number;
  activedCount: number;
  totalInvoicedPremium: number;
  totalComission: number;
  totalComissionBroker: number;
  totalComissionEstate: number;
  consolidadedApproved: number;
  consolidadedRejected: number;
}

export interface BondInsuranceResponse {
  data: BondInsuranceData[];
  total: number;
}

// Interface para Capitalização
export interface CapitalizationData {
  estateId: string;
  estateName: string;
  franchiseName: string;
  cpfCnpj?: string;
  totalCount: number;
  issuedCount: number;
  canceledCount: number;
  redemptionRequestedCount: number;
  redemptionCompletedCount: number;
  activeCount: number;
  totalAmount: number;
  totalComission: number;
  estateComission: number;
  brokerComission: number;
  managerName: string;
}

export interface CapitalizationResponse {
  data: CapitalizationData[];
  total: number;
}

// Interface para Seguro Incêndio
export interface FireInsuranceData {
  estateId: string;
  estateName: string;
  franchiseName: string;
  cpfCnpj?: string;
  issuedRenewedPolicies: number;
  activePolicies: number;
  totalQuotations: number;
  issuedPolicies: number;
  waitingPolicies: number;
  canceledPolicies: number;
  totalPremium: number;
  totalInvoicedPremium: number;
  totalComissionPremium: number;
  estateComissionPremium: number;
  brokerComissionPremium: number;
  managerName: string;
}

export interface FireInsuranceResponse {
  data: FireInsuranceData[];
  total: number;
}

// Interface para Sinistros
export interface ClaimsData {
  estateId: string;
  estateName: string;
  franchiseName: string;
  cpfCnpj?: string;
  openCount: number;
  agreementCount: number;
  analysisCount: number;
  inProgressCount: number;
  finishedCount: number;
  totalCount: number;
  managerName: string;
}

export interface ClaimsResponse {
  data: ClaimsData[];
  total: number;
}

// Interfaces para os dados dos gráficos do modal de resumo
export interface AnalysisEvolutionData {
  labels: string[];
  data: number[];
}

export interface ClientActivityData {
  labels: string[];
  data: number[];
}

export interface PeriodActivityData {
  labels: string[];
  data: number[];
}

// Formatação dos parâmetros para a URL da API
const formatParams = (params?: FilterParams): string => {
  if (!params) return '';

  const queryParams = [];
  if (params.limit) queryParams.push(`limit=${params.limit}`);
  if (params.franchiseId) queryParams.push(`franchiseId=${params.franchiseId}`);
  if (params.estateId) queryParams.push(`estateId=${params.estateId}`);
  if (params.estateUnitId) queryParams.push(`estateUnitId=${params.estateUnitId}`);
  if (params.managerId) queryParams.push(`managerId=${params.managerId}`);
  if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
  if (params.endDate) queryParams.push(`endDate=${params.endDate}`);

  return queryParams.length > 0 ? `&${queryParams.join('&')}` : '';
};

export const getDashboard = async (id: string, filter: string, params?: FilterParams) => {
  const queryParams = formatParams(params);
  const response = await axiosInstance.get(
    `/dashboard/${id}?filter=${filter}${queryParams}`
  );
  return response.data;
};

export const getAllDashboards = async (filter: string, params?: FilterParams) => {
  const queryParams = formatParams(params);
  const response = await axiosInstance.get(`/dashboard?filter=${filter}${queryParams}`);
  return response.data;
};

/**
 * Obtém dados de desempenho agrupados por imobiliária
 *
 * @param productType Tipo de produto (fianca, capitalizacao, incendio)
 * @param params Parâmetros de filtro opcionais
 * @param limit Limite máximo de imobiliárias a retornar (padrão: 7)
 * @returns Array de objetos com dados de desempenho por imobiliária, ordenados do maior para o menor
 */
export const getPerformanceByEstate = async (
  productType: string,
  filters: FilterParams = {},
  limit = 5
): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (filters.estateId) params.append('estateId', filters.estateId);
    if (filters.franchiseId) params.append('franchiseId', filters.franchiseId);
    if (filters.managerId) params.append('managerId', filters.managerId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (limit) params.append('limit', limit.toString());

    const response = await axiosInstance.get(
      `/dashboard/performance-by-estate?productType=${productType}&${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Erro ao buscar desempenho por imobiliária para ${productType}:`,
      error
    );
    return [];
  }
};

/**
 * Obtém dados consolidados para a tabela do dashboard
 *
 * @param params Parâmetros de filtro opcionais
 * @param limit Limite máximo de imobiliárias a retornar (padrão: 5)
 * @returns Objeto com dados consolidados por imobiliária e total de registros
 */
export const getTableData = async (
  filters: FilterParams = {},
  limit = 10
): Promise<TableDataResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters.estateId) params.append('estateId', filters.estateId);
    if (filters.franchiseId) params.append('franchiseId', filters.franchiseId);
    if (filters.managerId) params.append('managerId', filters.managerId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (limit) params.append('limit', limit.toString());
    if (filters.page) params.append('page', filters.page.toString());

    const response = await axiosInstance.get(
      `/dashboard/table-data?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados da tabela:', error);
    return { data: [], total: 0 };
  }
};

// Adicionar a função para buscar os dados do seguro fiança
export const getBondInsuranceData = async (params: {
  estateId?: string;
  franchiseId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
  page: number;
  limit: number;
}): Promise<BondInsuranceResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.estateId) {
      queryParams.append('estateId', params.estateId);
    }

    if (params.franchiseId) {
      queryParams.append('franchiseId', params.franchiseId);
    }

    if (params.managerId) {
      queryParams.append('managerId', params.managerId);
    }

    if (params.startDate) {
      queryParams.append('startDate', params.startDate);
    }

    if (params.endDate) {
      queryParams.append('endDate', params.endDate);
    }

    queryParams.append('page', params.page.toString());
    queryParams.append('limit', params.limit.toString());

    const response = await axiosInstance.get(
      `/dashboard/bond-insurance-data?${queryParams.toString()}`
    );

    return {
      data: response.data.data,
      total: response.data.total
    };
  } catch (error) {
    console.error('Erro ao buscar dados de seguro fiança:', error);
    return {
      data: [],
      total: 0
    };
  }
};

// Função para buscar dados de Capitalização
export const getCapitalizationData = async ({
  estateId,
  franchiseId,
  managerId,
  startDate,
  endDate,
  page = 1,
  limit = 10
}: {
  estateId?: string;
  franchiseId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<CapitalizationResponse> => {
  try {
    // Construir parâmetros de consulta
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (franchiseId) {
      params.append('franchiseId', franchiseId);
    }

    if (managerId) {
      params.append('managerId', managerId);
    }

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    if (estateId) {
      params.append('estateId', estateId);
    }

    const response = await axiosInstance.get(
      `/dashboard/capitalization-data?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de Capitalização:', error);
    return {
      data: [],
      total: 0
    };
  }
};

// Função para buscar dados de Seguro Incêndio
export const getFireInsuranceData = async ({
  estateId,
  franchiseId,
  managerId,
  startDate,
  endDate,
  page = 1,
  limit = 10
}: {
  estateId?: string;
  franchiseId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<FireInsuranceResponse> => {
  try {
    // Construir parâmetros de consulta
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (franchiseId) {
      params.append('franchiseId', franchiseId);
    }

    if (estateId) {
      params.append('estateId', estateId);
    }

    if (managerId) {
      params.append('managerId', managerId);
    }

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const response = await axiosInstance.get(
      `/dashboard/fire-insurance-data?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de Seguro Incêndio:', error);
    return {
      data: [],
      total: 0
    };
  }
};

// Função para buscar dados de Sinistros
export const getClaimsData = async ({
  estateId,
  franchiseId,
  managerId,
  startDate,
  endDate,
  page = 1,
  limit = 10
}: {
  estateId?: string;
  franchiseId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<ClaimsResponse> => {
  try {
    // Construir parâmetros de consulta
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (franchiseId) {
      params.append('franchiseId', franchiseId);
    }

    if (estateId) {
      params.append('estateId', estateId);
    }

    if (managerId) {
      params.append('managerId', managerId);
    }

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    const response = await axiosInstance.get(
      `/dashboard/claims-data?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de Sinistros:', error);
    return {
      data: [],
      total: 0
    };
  }
};

export const downloadDashboardReport = async (params: {
  estateId?: string;
  franchiseId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ success: boolean; message: string }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.estateId) {
      queryParams.append('estateId', params.estateId);
    }

    if (params.franchiseId) {
      queryParams.append('franchiseId', params.franchiseId);
    }

    if (params.managerId) {
      queryParams.append('managerId', params.managerId);
    }

    if (params.startDate) {
      queryParams.append('startDate', params.startDate);
    }

    if (params.endDate) {
      queryParams.append('endDate', params.endDate);
    }

    const response = await axiosInstance.get(
      `/dashboard/download-report?${queryParams.toString()}`
    );

    return {
      success: true,
      message:
        response.data.message ||
        'Seu relatório está sendo processado e será enviado para o seu e-mail em breve.'
    };
  } catch (error) {
    console.error('Erro ao solicitar relatório do dashboard:', error);
    return {
      success: false,
      message:
        'Ocorreu um erro ao solicitar o relatório. Por favor, tente novamente mais tarde.'
    };
  }
};

// Funções para buscar dados do dashboard
export async function getDashboardData({
  franchiseId,
  estateId,
  managerId,
  startDate,
  endDate
}: FilterParams) {
  const filter = {
    ...(franchiseId && { franchiseId }),
    ...(estateId && { estateId }),
    ...(managerId && { managerId })
  };

  const queryParams = [
    startDate && `startDate=${startDate}`,
    endDate && `endDate=${endDate}`
  ]
    .filter(Boolean)
    .join('&');

  const queryString = queryParams ? `&${queryParams}` : '';
  const filterParam = Object.keys(filter).length
    ? `filter=${JSON.stringify(filter)}`
    : '';
  const urlParams = filterParam
    ? `?${filterParam}${queryString}`
    : queryParams
    ? `?${queryParams.substring(1)}`
    : '';

  const { data } = await axiosInstance.get(`/dashboard/${urlParams}`);
  return data;
}

// Busca dados detalhados de seguros fiança
export async function getBondInsuranceDetails({
  franchiseId,
  estateId,
  managerId,
  startDate,
  endDate
}: FilterParams): Promise<BondInsuranceDetailData> {
  const filter = {
    ...(franchiseId && { franchiseId }),
    ...(estateId && { estateId }),
    ...(managerId && { managerId })
  };

  const queryParams = [
    startDate && `startDate=${startDate}`,
    endDate && `endDate=${endDate}`
  ]
    .filter(Boolean)
    .join('&');

  const queryString = queryParams ? `&${queryParams}` : '';
  const filterParam = Object.keys(filter).length
    ? `filter=${JSON.stringify(filter)}`
    : '';
  const urlParams = filterParam
    ? `?${filterParam}${queryString}`
    : queryParams
    ? `?${queryParams.substring(1)}`
    : '';

  const { data } = await axiosInstance.get(
    `/dashboard/bond-insurance-details${urlParams}`
  );
  return data;
}

// Função para buscar dados de evolução das análises (últimos 30 dias)
export const getAnalysisEvolution = async (
  productType: string,
  params?: {
    estateId?: string;
    franchiseId?: string;
    managerId?: string;
    startDate?: string;
    endDate?: string;
  }
): Promise<AnalysisEvolutionData> => {
  try {
    let url = `/dashboard/analysis-evolution?productType=${productType}`;

    if (params?.estateId && params.estateId !== 'Todos') {
      url += `&estateId=${params.estateId}`;
    }

    if (params?.franchiseId && params.franchiseId !== 'Todos') {
      url += `&franchiseId=${params.franchiseId}`;
    }

    if (params?.managerId && params.managerId !== 'Todos') {
      url += `&managerId=${params.managerId}`;
    }

    if (params?.startDate) {
      url += `&startDate=${params.startDate}`;
    }

    if (params?.endDate) {
      url += `&endDate=${params.endDate}`;
    }

    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de evolução das análises:', error);
    return { labels: [], data: [] };
  }
};

// Função para buscar dados de atividade do cliente (últimos 30 dias)
export const getClientActivity = async (
  productType: string,
  params?: {
    estateId?: string;
    franchiseId?: string;
    managerId?: string;
    startDate?: string;
    endDate?: string;
  }
): Promise<ClientActivityData> => {
  try {
    let url = `/dashboard/client-activity?productType=${productType}`;

    if (params?.estateId && params.estateId !== 'Todos') {
      url += `&estateId=${params.estateId}`;
    }

    if (params?.franchiseId && params.franchiseId !== 'Todos') {
      url += `&franchiseId=${params.franchiseId}`;
    }

    if (params?.managerId && params.managerId !== 'Todos') {
      url += `&managerId=${params.managerId}`;
    }

    if (params?.startDate) {
      url += `&startDate=${params.startDate}`;
    }

    if (params?.endDate) {
      url += `&endDate=${params.endDate}`;
    }

    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de atividade do cliente:', error);
    return { labels: [], data: [] };
  }
};

// Função para buscar dados de atividade no período (últimos 12 meses)
export const getPeriodActivity = async (
  productType: string,
  params?: {
    estateId?: string;
    franchiseId?: string;
    managerId?: string;
    startDate?: string;
    endDate?: string;
  }
): Promise<PeriodActivityData> => {
  try {
    let url = `/dashboard/period-activity?productType=${productType}`;

    if (params?.estateId && params.estateId !== 'Todos') {
      url += `&estateId=${params.estateId}`;
    }

    if (params?.franchiseId && params.franchiseId !== 'Todos') {
      url += `&franchiseId=${params.franchiseId}`;
    }

    if (params?.managerId && params.managerId !== 'Todos') {
      url += `&managerId=${params.managerId}`;
    }

    if (params?.startDate) {
      url += `&startDate=${params.startDate}`;
    }

    if (params?.endDate) {
      url += `&endDate=${params.endDate}`;
    }

    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de atividade no período:', error);
    return { labels: [], data: [] };
  }
};

export interface IntegrationEfficiencyData {
  totalRecords: number;
  fullyIntegrated: number;
  fullyManual: number;
  fallback: number;
  percentages: {
    fullyIntegrated: string;
    fullyManual: string;
    fallback: string;
  };
}

export interface IntegrationStatusData {
  statusDistribution: {
    labels: string[];
    data: number[];
  };
  productData: {
    labels: string[];
    datasets: any[];
  };
}

export interface IntegrationTrendData {
  labels: string[];
  datasets: any[];
}

export interface IntegrationsByProductData {
  fireInsurance: { integrated: number; manual: number; fallback: number };
  bondInsurance: { integrated: number; manual: number; fallback: number };
  capitalization: { integrated: number; manual: number; fallback: number };
}

export interface FallbackCasesData {
  data: any[];
  total: number;
}

export interface IntegrationFilterParams {
  estateId?: string;
  franchiseId?: string;
  managerId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  page?: number;
}

// Funções para o dashboard de integrações
export const getIntegrationEfficiencyData = async (
  params: IntegrationFilterParams = {}
): Promise<IntegrationEfficiencyData> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.estateId) queryParams.append('estateId', params.estateId);
    if (params.franchiseId) queryParams.append('franchiseId', params.franchiseId);
    if (params.managerId) queryParams.append('managerId', params.managerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await axiosInstance.get(
      `/dashboard/integration-efficiency?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de eficiência das integrações:', error);
    throw error;
  }
};

export const getIntegrationStatusData = async (
  params: IntegrationFilterParams = {}
): Promise<IntegrationStatusData> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.estateId) queryParams.append('estateId', params.estateId);
    if (params.franchiseId) queryParams.append('franchiseId', params.franchiseId);
    if (params.managerId) queryParams.append('managerId', params.managerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await axiosInstance.get(
      `/dashboard/integration-status?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de status das integrações:', error);
    throw error;
  }
};

export const getIntegrationTrendData = async (
  params: IntegrationFilterParams = {}
): Promise<IntegrationTrendData> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.estateId) queryParams.append('estateId', params.estateId);
    if (params.franchiseId) queryParams.append('franchiseId', params.franchiseId);
    if (params.managerId) queryParams.append('managerId', params.managerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await axiosInstance.get(
      `/dashboard/integration-trend?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados de tendência das integrações:', error);
    throw error;
  }
};

export const getIntegrationsByProduct = async (
  params: IntegrationFilterParams = {}
): Promise<IntegrationsByProductData> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.estateId) queryParams.append('estateId', params.estateId);
    if (params.franchiseId) queryParams.append('franchiseId', params.franchiseId);
    if (params.managerId) queryParams.append('managerId', params.managerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await axiosInstance.get(
      `/dashboard/integrations-by-product?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados por produto das integrações:', error);
    throw error;
  }
};

export const getFallbackCases = async (
  params: IntegrationFilterParams = {}
): Promise<FallbackCasesData> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.estateId) queryParams.append('estateId', params.estateId);
    if (params.franchiseId) queryParams.append('franchiseId', params.franchiseId);
    if (params.managerId) queryParams.append('managerId', params.managerId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await axiosInstance.get(
      `/dashboard/fallback-cases?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar casos de fallback:', error);
    throw error;
  }
};
